import { FlowObjectType, FlowObjectDefinition, } from '../../../../models/flow-object.model';
import { Component, OnInit, Input, EventEmitter, Output, ElementRef, ViewChild, HostListener, TemplateRef, signal, OnChanges, computed, NO_ERRORS_SCHEMA } from '@angular/core';
import { Enums } from '../../../../shared/enums';
import { ToastrService } from 'ngx-toastr';
import { FlowDefinition } from '../../../../models/flow.model';
import { Utils } from '../../../../shared/utils';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { FlowObjectDetailsDocumentComponent } from '../document/fod-document.component';
import { FlowObjectDetailsForwardComponent } from '../forward/fod-forward.component';
import { FlowObjectDetailsRegisterProcessComponent } from '../register-process/fod-register-process.component';
import { FOD_DEFAULT_TINYMCE_OPTIONS } from './tinymce-options';
import { FOD_START_INBOUND_API_TINYMCE_OPTIONS } from '../start-inbound-api/tinymce-options';
import { FlowObjectDetailsDispatchProcessComponent } from '../dispatch-process/fod-dispatch-process.component';
import { ConfigSchema } from '../../../../models/config-schema.model';
import { IMPORTS } from './_imports';
import { FlowObjectDetailsAddToProcessComponent } from '../add-to-process/fod-add-to-process.component';

@Component({
    selector: 'fod-default',
    templateUrl: './fod-default.component.html',
    styleUrl: './fod-default.component.scss',
    standalone: true,
    imports: IMPORTS,
    schemas: [NO_ERRORS_SCHEMA]
})
export class FlowObjectDetailsDefaultComponent implements OnInit, OnChanges {
    // #region [ViewChild]
    @ViewChild('configSchemaRef') configSchemaRef: TemplateRef<HTMLElement>;
    @ViewChild('configSchemaEditorRef') configSchemaEditorRef: ElementRef;
    @ViewChild('flowObjectDetailsDocumentRef') flowObjectDetailsDocumentRef: FlowObjectDetailsDocumentComponent;
    @ViewChild('flowObjectDetailsForwardRef') flowObjectDetailsForwardRef: FlowObjectDetailsForwardComponent;
    @ViewChild('flowObjectDetailsRegisterProcessRef') flowObjectDetailsRegisterProcessRef: FlowObjectDetailsRegisterProcessComponent;
    @ViewChild('flowObjectDetailsDispatchProcessRef') flowObjectDetailsDispatchProcessRef: FlowObjectDetailsDispatchProcessComponent;
    @ViewChild('flowObjectDetailsAddToProcessRef') flowObjectDetailsAddToProcessRef: FlowObjectDetailsAddToProcessComponent;
    // #endregion

    // #region [listeners]
    @HostListener('keydown', ['$event'])
    onKeyDown(event: KeyboardEvent) {
        if (event.key.toLowerCase() == 'enter') {
            event.preventDefault();
            event.stopPropagation();
        }
    }
    // #endregion

    // #region [Type props]
    FlowObjectType = FlowObjectType;
    Utils = Utils;
    // #endregion

    // #region [props]
    configSchemaDialog: MatDialogRef<HTMLElement, any>;
    // #endregion

    // #region [signals]
    $model = signal(null as FlowObjectDefinition);
    $flowDefinition = signal(null as FlowDefinition);
    $isReadOnlyMode = signal(false);
    $ongoingTinyMceRecycle = signal(false);
    $recipientMode = signal(Enums.RecipientMode.Fixed);
    $processOrigin = signal(Enums.ProcessOrigin.Instance);
    $configSchema = signal(null as ConfigSchema);
    $configSchemaFlat = signal(null as string);
    $publicMessageHtml = signal(null as string);
    $name = signal(null as string);
    $publicName = signal(null as string);
    $tinyMceOptions = signal(null as any);
    $isProcessProtocolValid = signal(true);
    // #endregion

    // #region [computeds]
    $shouldShowPublicMessageHtml = computed(() => !this.$ongoingTinyMceRecycle()
        && (
            this.$isPublicMessageHtmlBefore()
            || this.$isPublicMessageHtmlAfter()
        )
    );
    $isPublicMessageHtmlBefore = computed(() =>
        [
            FlowObjectType.StartInboundApi,
            FlowObjectType.GatewayApprove,
            FlowObjectType.TaskAcknowledge,
            FlowObjectType.TaskRegisterProcess,
            FlowObjectType.TaskDispatchProcess,
            FlowObjectType.TaskAddToProcess
        ].includes(this.$model()?.typeId)
    );
    $isPublicMessageHtmlAfter = computed(() =>
        [
            FlowObjectType.Finish,
            FlowObjectType.FinishContent,
            FlowObjectType.FinishOutboundApi,
        ].includes(this.$model()?.typeId)
    );
    $shouldShowFlowObjectDetailsDocument = computed(() =>
        [
            FlowObjectType.StartForm,
            FlowObjectType.GatewayApprove,
            FlowObjectType.TaskAcknowledge
        ].includes(this.$model()?.typeId)
    );
    $isRadioSelectionValid = computed(() =>
        (
            this.$model().typeId == FlowObjectType.TaskForward
            && (
                (this.$recipientMode() == Enums.RecipientMode.ImmediateSupervisor && this.$configSchema()?.taskForward?.toImmediateSupervisor == true)
                || (this.$recipientMode() == Enums.RecipientMode.OwnerImmediateSupervisor && this.$configSchema()?.taskForward?.toOwnerImmediateSupervisor == true)
                || (this.$recipientMode() == Enums.RecipientMode.OwnerHeadOfOrganization && this.$configSchema()?.taskForward?.toOwnerHeadOfOrganization == true)
                || (this.$recipientMode() == Enums.RecipientMode.OwnRole && this.$configSchema()?.taskForward?.toOwnRole == true)
                || (this.$recipientMode() == Enums.RecipientMode.Fixed && Utils.isNotNullOrEmpty(this.$configSchema()?.taskForward?.recipient.id))
                || (this.$recipientMode() == Enums.RecipientMode.Conditional && this.$configSchema()?.taskForward?.conditionalRecipients.length > 0)
            )
        ) || (
            this.$model().typeId == FlowObjectType.TaskDispatchProcess
            && (
                (this.$recipientMode() == Enums.RecipientMode.PublicAgent && this.$configSchema()?.taskDispatchProcess?.toPublicAgent)
                || (this.$recipientMode() == Enums.RecipientMode.OwnerImmediateSupervisor && this.$configSchema()?.taskDispatchProcess?.toOwnerImmediateSupervisor == true)
                || (this.$recipientMode() == Enums.RecipientMode.OwnerHeadOfOrganization && this.$configSchema()?.taskDispatchProcess?.toOwnerHeadOfOrganization == true)
                || (this.$recipientMode() == Enums.RecipientMode.Fixed && Utils.isNotNullOrEmpty(this.$configSchema()?.taskDispatchProcess?.dispatchRecipient.id))
                || (this.$recipientMode() == Enums.RecipientMode.Conditional && this.$configSchema()?.taskDispatchProcess?.conditionalRecipients.length > 0)
            )
        ) || (
            this.$model().typeId == FlowObjectType.TaskAddToProcess
            && (
                (this.$processOrigin() == Enums.ProcessOrigin.Instance && this.$configSchema()?.taskAddToProcess?.processOrigin == Enums.ProcessOrigin.Instance)
                || (this.$processOrigin() == Enums.ProcessOrigin.PublicAgentDeliberation && this.$configSchema()?.taskAddToProcess?.processOrigin == Enums.ProcessOrigin.PublicAgentDeliberation)
                || (this.$processOrigin() == Enums.ProcessOrigin.Form && this.$configSchema()?.taskAddToProcess?.processOrigin == Enums.ProcessOrigin.Form && Utils.isNotNullOrEmpty(this.$configSchema()?.taskAddToProcess?.processProtocolFormField))
            )
        )
    );
    // #endregion

    // #region [Input/Output]
    @Input() _model: FlowObjectDefinition;
    @Input() _flowDefinition: FlowDefinition;
    @Input() _isReadOnlyMode: boolean;
    @Input() _ongoingTinyMceRecycle: boolean;
    @Output() _submitEvent = new EventEmitter<FlowObjectDefinition>();
    @Output() _closeEvent = new EventEmitter<any>();
    // #endregion

    constructor(
        private dialog: MatDialog,
        private toastr: ToastrService
    ) { }

    ngOnInit() {
        this.$publicMessageHtml.set(this.$model().publicMessageHtml);
        this.$name.set(this.$model().name);
        this.$publicName.set(this.$model().publicName);

        if (this.$model().typeId == FlowObjectType.StartInboundApi) {
            Utils.setSignal(this.$tinyMceOptions, FOD_START_INBOUND_API_TINYMCE_OPTIONS);
        } else {
            Utils.setSignal(this.$tinyMceOptions, FOD_DEFAULT_TINYMCE_OPTIONS);
        }

        if (this.$model().configSchema != null) {
            Utils.setSignal(this.$configSchema, Object.assign(new ConfigSchema(), JSON.parse(this.$model().configSchema)));
        }
    }

    ngOnChanges() {
        this.$model.set(this._model);
        this.$flowDefinition.set(this._flowDefinition);
        this.$isReadOnlyMode.set(this._isReadOnlyMode);
        this.$ongoingTinyMceRecycle.set(this._ongoingTinyMceRecycle);
    }

    updateRecipientMode(event) {
        this.$recipientMode.set(event);
    }

    updateProcessOrigin(event) {
        this.$processOrigin.set(event);
    }

    showConfigSchema() {
        if (
            this.$model().typeId == FlowObjectType.TaskRegisterProcess
            && !this.flowObjectDetailsRegisterProcessRef.resolveProcessInfo()
        ) return;

        Utils.updSignal(this.$model, x => {
            x.publicMessageHtml = this.$publicMessageHtml();
            return x;
        });

        let tempObject = {
            model: JSON.parse(JSON.stringify(this.$model())) as FlowObjectDefinition,
            configSchema: JSON.parse(JSON.stringify(this.$configSchema()))
        };
        delete tempObject.model.next;

        this.$configSchemaFlat.set(btoa(encodeURIComponent(JSON.stringify(tempObject))));

        this.configSchemaDialog = this.dialog.open(this.configSchemaRef, {
            minWidth: '800px'
        });

        this.configSchemaDialog.afterOpened().subscribe(() => {
            setTimeout(() => {
                this.configSchemaEditorRef.nativeElement.scrollLeft = 0;
                this.configSchemaEditorRef.nativeElement.scrollTop = 0;
            }, 1);
            setTimeout(() => {
                this.configSchemaEditorRef.nativeElement.style.opacity = '1';
            }, 100);
        });

        this.configSchemaDialog.afterClosed().subscribe(async () => {
            if (this.$isReadOnlyMode()) return;

            try {
                let modalObject = JSON.parse(decodeURIComponent(atob(this.configSchemaEditorRef.nativeElement.value)));
                let modalObjectPrint = JSON.stringify(modalObject);
                delete modalObject.model.configSchema;

                // checa se o tipo da origem do conteúdo é o mesmo tipo do conteúdo atual e se a origem não é um FlowDefinition
                if (modalObject.model.typeId != this.$model().typeId || modalObject.model.targetId != null) {
                    this.toastr.error(Enums.Messages.InvalidConfigSchemaSourceType, Enums.Messages.Error, Utils.getToastrErrorOptions());
                    return;
                }

                // checa se o código-fonte foi alterado
                if (JSON.stringify(tempObject) != modalObjectPrint) {
                    this.$configSchema.set(JSON.parse(JSON.stringify(modalObject.configSchema)) as ConfigSchema);

                    Utils.updSignal(this.$model, x => {
                        x.name = modalObject.model.name;
                        x.publicName = modalObject.model.publicName;
                        x.publicMessageHtml = modalObject.model.publicMessageHtml;
                        return x;
                    });

                    this.$name.set(this.$model().name);
                    this.$publicName.set(this.$model().publicName);
                    this.$publicMessageHtml.set(this.$model().publicMessageHtml);

                    switch (this.$model().typeId) {
                        case FlowObjectType.TaskForward:
                            this.flowObjectDetailsForwardRef.updateSourceCode(this.$configSchema());
                            break;

                        case FlowObjectType.TaskRegisterProcess:
                            this.flowObjectDetailsRegisterProcessRef.updateSourceCode(this.$configSchema());
                            break;

                        case FlowObjectType.TaskDispatchProcess:
                            this.flowObjectDetailsDispatchProcessRef.updateSourceCode(this.$configSchema());
                            break;

                        case FlowObjectType.TaskAddToProcess:
                            this.flowObjectDetailsAddToProcessRef.updateSourceCode(this.$configSchema());
                            break;

                        case FlowObjectType.StartForm:
                        case FlowObjectType.GatewayApprove:
                        case FlowObjectType.TaskAcknowledge:
                            this.flowObjectDetailsDocumentRef.updateSourceCode(this.$configSchema());
                            break;

                        default:
                            break;
                    }
                }
            } catch (error) {
                this.toastr.error(Enums.Messages.InvalidFlowObjectConfigSchemaSource, Enums.Messages.Error, Utils.getToastrErrorOptions());
                return;
            }
        });
    }

    onSubmit() {
        if (
            this.$isReadOnlyMode()
            || (
                this.$model().typeId == FlowObjectType.TaskForward
                && !this.$isRadioSelectionValid()
            )
            || (
                this.$model().typeId == FlowObjectType.TaskAddToProcess
                && !this.$isProcessProtocolValid()
            )
        ) return false;

        if (
            (
                this.$model().typeId == FlowObjectType.TaskRegisterProcess
                && !this.flowObjectDetailsRegisterProcessRef.resolveProcessInfo()
            ) || (
                this.$model().typeId == FlowObjectType.TaskDispatchProcess
                && !this.flowObjectDetailsDispatchProcessRef.resolveDispatchInfo()
            ) || (
                this.$model().typeId == FlowObjectType.TaskAddToProcess
                && !this.flowObjectDetailsAddToProcessRef.resolveAddToProcessInfo()
            )
        ) return false;

        if (this.$name().trim().length < 3) {
            this.toastr.warning(Enums.Messages.NameMinLength, Enums.Messages.Warning, Utils.getToastrErrorOptions());
            return false;
        }

        Utils.updSignal(this.$model, x => {
            x.configSchema = JSON.stringify(this.$configSchema());
            x.publicMessageHtml = this.$publicMessageHtml();
            x.name = this.$name();
            x.publicName = this.$publicName();
            return x;
        });

        this._submitEvent.emit(this.$model());
    }

    closeForm() {
        this._closeEvent.emit();
    }

    // #region [===== private methods =====]
    // #endregion
}
